import { useAuth0 } from "@auth0/auth0-react";
import { ReactComponent as Close } from "assets/icons/CloseFilter.svg";
import BottomArrowSvg from "assets/icons/svg/BottomArrowSvg";
import Button from "components/button/Button";
import { LabelField } from "components/LabelField";
import { MenuMultiCheckBox } from "components/MenuMultiCheckBox/MenuMultiCheckBox";
import addDays from "date-fns/addDays";
import { getDateFormat, getDateFormatForDatePicket } from "helper/dateFormat";
import FinanceService from "Lib/API/Finance/index";
import React, { useEffect, useState } from "react";
import { toastr } from "react-redux-toastr";
import Select, { components } from "react-select";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import { filterList, predefinedRanges } from "./DonationFilterData";
import NumberInput from "./NumberInput/NumberInput";
import {
  CloseButtonWrapper,
  DonationAmountRange,
  FilterClearButton,
  FilterCloseButton,
  FilterColumn,
  FilterGoFilterButton,
  FilterRows,
  FilterWrapperButtons,
} from "./Theme";
import {
  convertLocalRangeToUTC,
  convertUTCRangeToLocal,
} from "utils/dateRange";

const selectControlStyles = {
  backgroundColor: "#FFFFFF",
  borderColor: "#FFFFFF",
  color: "#b5b5c3",
  transition:
    "color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
  paddingTop: "6px",
  paddingBottom: "6px",
  cursor: "pointer",
};
const colourStyles = {
  control: (styles) => ({
    ...styles,
    ...selectControlStyles,
  }),
  menu: (styles) => ({
    ...styles,
    zIndex: 9999,
  }),
};

const { beforeToday, allowedRange } = DateRangePicker;
const formatOptionLabel = ({ label, _id }) => {
  return (
    <div className="dropdown-container" key={_id}>
      <div className="icon-name-container d-flex align-items-center justify-content-start">
        <div className="name-container" style={{ marginInlineStart: 10 }}>
          <div>{label}</div>
        </div>
      </div>
    </div>
  );
};

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <BottomArrowSvg label="Emoji" />
    </components.DropdownIndicator>
  );
};

const DonationFilter = ({
  closeFilter,
  formik,
  clearFilter,
  handleFilter,
  isFilterApplied,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const placeholderFormat = getDateFormat();
  const dateFormate = getDateFormatForDatePicket();

  const [ledgers, setLedgers] = useState([]);
  const [isLedgerLoading, setIsLedgerLoading] = useState(false);

  const getLedgerAccounts = async () => {
    setIsLedgerLoading(true);
    const token = await getAccessTokenSilently();
    FinanceService.getLedgerSubLedgerListAll(token)
      .then((res) => {
        if (res?.ledgers) {
          const arrayWithSelectAll = ["Select All", ...res?.ledgers];
          setLedgers(arrayWithSelectAll);
        }
      })
      .catch((error) => {
        toastr.error("Error", error?.data?.message ?? "Something went wrong");
      })
      .finally(() => {
        setIsLedgerLoading(false);
      });
  };

  useEffect(() => {
    getLedgerAccounts();
  }, []);

  const displayFilter = (filter) => {
    switch (filter.type) {
      case "date-range":
        return (
          <>
            <LabelField title={filter.label} />
            <DateRangePicker
              format={dateFormate}
              placeholder={`${placeholderFormat} - ${placeholderFormat}`}
              cleanable={true}
              style={{
                display: "inline-block",
                padding: "0",
                border: "0",
                width: "100%",
                color: "black",
              }}
              size="lg"
              value={convertUTCRangeToLocal(
                formik?.values?.filterValues?.[filter.name] ?? [null, null]
              )}
              onKeyPress={(event) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              disabledDate={allowedRange(
                addDays(new Date(), -364),
                addDays(new Date(), 364)
              )}
              ranges={predefinedRanges}
              onChange={(update) => {
                const utcRange = convertLocalRangeToUTC(update);
                formik.setFieldValue(`filterValues.${filter.name}`, utcRange);
              }}
            />
          </>
        );
      case "select":
        return (
          <>
            <LabelField title={filter.label} />
            <Select
              {...{
                name: filter.name,
              }}
              classNamePrefix="react-select"
              formatOptionLabel={formatOptionLabel}
              options={filter.list}
              styles={colourStyles}
              onChange={(value) => {
                if (!value) {
                  formik.setFieldValue(`filterValues.${filter.name}`, null);
                }
                formik.setFieldValue(
                  `filterValues.${filter.name}`,
                  value?.value
                );
              }}
              value={
                formik?.values?.filterValues?.[filter.name]
                  ? filter.list.find(
                      (item) =>
                        item.value ===
                          formik?.values?.filterValues?.[filter.name] || null
                    )
                  : null
              }
              components={{ DropdownIndicator }}
              isClearable={
                formik?.values?.filterValues?.[filter.name] ? true : false
              }
            />
          </>
        );
      case "number-range":
        return (
          <DonationAmountRange>
            <LabelField title={filter.label} />
            <div className="d-flex align-items-center" style={{ gap: 10 }}>
              <NumberInput
                value={formik?.values?.filterValues?.[filter.name]?.min}
                onChange={(value) =>
                  formik.setFieldValue(`filterValues.${filter.name}.min`, value)
                }
                label="Min"
              />
              <span>-</span>
              <NumberInput
                value={formik?.values?.filterValues?.[filter.name]?.max}
                onChange={(value) =>
                  formik.setFieldValue(`filterValues.${filter.name}.max`, value)
                }
                label="Max"
              />
            </div>
          </DonationAmountRange>
        );
      case "ledger":
        return (
          <>
            <LabelField title={filter.label} />
            <MenuMultiCheckBox
              Items={ledgers}
              formik={formik}
              isLedger={true}
              withBorder={true}
              field="ledgers"
              width="100%"
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <CloseButtonWrapper>
        <FilterCloseButton onClick={closeFilter} className="filter-button">
          <Close />
        </FilterCloseButton>
      </CloseButtonWrapper>
      <FilterRows className="row">
        {filterList.map((filter) => (
          <FilterColumn
            className={` ${
              filter.className ? filter.className : "col-6 col-sm-4 col-md-3"
            }`}
            key={filter.name}
          >
            {displayFilter(filter)}
          </FilterColumn>
        ))}
      </FilterRows>
      <FilterWrapperButtons>
        <Button
          className="mr-3"
          onClick={clearFilter}
          disabled={!isFilterApplied}
          variant="primary-outline"
          size="md"
          style={{ minWidth: 100 }}
        >
          Clear
        </Button>
        <Button
          className="filter-button"
          onClick={handleFilter}
          disabled={!isFilterApplied}
          variant="primary"
          size="md"
          style={{ minWidth: 100 }}
        >
          {" "}
          {"Filter"}
        </Button>
      </FilterWrapperButtons>
    </>
  );
};

export default DonationFilter;
